<template>
  <div class="py-3 border-b border-gray-200">
    <div class="flex justify-between items-center gap-6 mb-2">
      <div>
        <div class="text-sm font-medium text-gray-900">
          {{ useT('auth.trustedDevices.title') }}
        </div>
        <p class="text-xs text-gray-500 leading-snug mt-0.5">
          {{ useT('auth.trustedDevices.description') }}
        </p>
      </div>
    </div>

    <div
      v-if="isLoading"
      v-loading="isLoading"
      class="w-full h-20"
    />

    <div
      v-else-if="!devices.length"
      class="text-xs text-gray-400 py-3"
    >
      {{ useT('auth.trustedDevices.empty') }}
    </div>

    <ul
      v-else
      class="divide-y divide-gray-100"
    >
      <li
        v-for="device in devices"
        :key="device._id"
        class="flex items-center justify-between gap-4 py-2"
      >
        <div class="min-w-0">
          <div class="text-sm text-gray-900 truncate">
            {{ describeDevice(device) }}
            <span
              v-if="device.current"
              class="text-xs text-primary-600 font-medium ml-1"
            >
              {{ useT('auth.trustedDevices.current') }}
            </span>
          </div>
          <div class="text-xs text-gray-500">
            {{ useT('auth.trustedDevices.meta', {
              ip: device.ip || '—',
              lastUsed: getFormattedDate(device.lastUsedAt, { lang: useAuthStore().user?.locale, format: 'lll' }),
            }) }}
          </div>
        </div>

        <Button
          type="danger-text"
          size="small"
          :loading="revokingId === device._id"
          @click="revoke(device)"
        >
          {{ useT('auth.trustedDevices.revoke') }}
        </Button>
      </li>
    </ul>
  </div>
</template>

<script setup>
// Refs
const devices = ref([]);
const isLoading = ref(false);
const revokingId = ref(null);

// Methods
const fetchDevices = async() => {
  isLoading.value = true;
  try {
    // Sent so the API can flag which row is the browser you're sitting in — it
    // only ever compares hashes. In a header rather than the query string,
    // because a query param would leak this 2FA-bypass credential to the log.
    const deviceToken = useCookie('deviceToken').value;
    const response = await useApi.get('/users/me/trusted-devices', {
      headers: deviceToken ? { 'X-Device-Token': deviceToken } : {},
    });
    devices.value = response.results || [];
  } catch (error) {
    console.error(error);
    useToast.error(useT('auth.trustedDevices.toast.loadError'));
  } finally {
    isLoading.value = false;
  }
};

// User agents are long and mostly noise; the browser and OS are what makes a
// device recognisable in a list.
const describeDevice = (device) => {
  const userAgent = device.userAgent || '';
  const browser = ['Edg', 'OPR', 'Chrome', 'Firefox', 'Safari'].find((name) => userAgent.includes(name));
  const platform = ['Windows', 'Macintosh', 'Linux', 'Android', 'iPhone', 'iPad'].find((name) => userAgent.includes(name));

  const label = [
    { Edg: 'Edge', OPR: 'Opera' }[browser] || browser,
    { Macintosh: 'macOS', iPhone: 'iOS', iPad: 'iPadOS' }[platform] || platform,
  ].filter(Boolean).join(' · ');

  return label || useT('auth.trustedDevices.unknown');
};

const revoke = async(device) => {
  revokingId.value = device._id;

  try {
    await useApi.delete(`/users/me/trusted-devices/${device._id}`);
    useToast(useT('auth.trustedDevices.toast.revoked'));

    // Revoking the browser you're in means the next login needs a code again.
    if (device.current) {
      useCookie('deviceToken').value = null;
    }

    await fetchDevices();
  } catch (error) {
    console.error(error);
    useToast.error(useT('auth.trustedDevices.toast.revokeError'));
  } finally {
    revokingId.value = null;
  }
};

// Lifecycle
onMounted(fetchDevices);
</script>
